/** @jsx jsx */
import React, { forwardRef } from 'react';

import { Inline, Flex, Stack, Text, Box } from '@atlaskit/primitives/compiled';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { token } from '@atlaskit/tokens';
import TeamsIcon from '@atlaskit/icon/core/teams';
import LinkIcon from '@atlaskit/icon/core/link';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import ScorecardIcon from '@atlaskit/icon/core/scorecard';
import Heading from '@atlaskit/heading';
import { cssMap, jsx } from '@atlaskit/css';
import PulseIcon from '@atlaskit/icon/core/pulse';
import AddIcon from '@atlaskit/icon/core/add';
import Tooltip, { type TooltipPrimitiveProps, TooltipPrimitive } from '@atlaskit/tooltip';

import { IconButton } from '@atlaskit/button/new';
import { expVal } from '@atlassian/jira-feature-experiments';

import { ComponentTypeIcon } from '@atlassian/jira-compass-common/src/ui/component-type-icons/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import ComponentTypesMetadata from '@atlassian/jira-compass-common/src/common/ui/component-types-metadata/index.tsx';
import { messages } from './messages.tsx';

type Props = {
	repositoryName: string;
	cardVisualConfigurationIndex?: number;
};
type IconType = keyof typeof ComponentTypesMetadata | 'OTHER';
function isIconType(raw: string): raw is IconType {
	return raw in ComponentTypesMetadata;
}
const toComponentType = (raw: string): IconType => (isIconType(raw) ? raw : 'OTHER');

const styles = cssMap({
	cardStyles: {
		display: 'flex',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		maxWidth: '400px',
		minWidth: '324px',
	},
	containerStyle: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'nowrap',
		alignItems: 'center',
		height: '100%',
	},
	unpressableButton: {
		borderRadius: token('radius.small'),
		color: token('color.text.subtle'),
		display: 'inline-flex',
		backgroundColor: token('color.background.neutral'),
		flexDirection: 'row',
		flexWrap: 'nowrap',
		paddingRight: token('space.200'),
		paddingLeft: token('space.100'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
	},
	iconBox: {
		paddingLeft: token('space.025'),
		paddingRight: token('space.050'),
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
	},
	inlineTooltip: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
		color: token('color.text'),
		maxHeight: '300px',
		maxWidth: '300px',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
	},
	informationIcon: {
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
	},
	buttonStyle: {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	completedActions: {
		paddingLeft: token('space.300'),
	},
});

const CustomTooltip = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(
	({ children, className, ...rest }, ref) => {
		return (
			<TooltipPrimitive
				{...rest}
				// Manually passing on `className` so it gets merged correctly in the build output.
				// The passed classname is mostly used for integration testing (`.Tooltip`)
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-classname-prop
				className={className}
				// "css" does not "exist" - it gets transformed into "className" by compiled
				css={styles.inlineTooltip}
				ref={ref}
				placement="top"
			>
				{children}
			</TooltipPrimitive>
		);
	},
);

function CompassRepositoryCard(props: Props): JSX.Element {
	const projectAddedGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.projectAddedGalaxia
		: messages.projectAdded;
	const testId = 'project-directories.components.view.compass-repository-card';
	const { formatMessage } = useIntl();
	const configuration = [
		{
			componentType: 'OTHER',
			scorecard: {
				icon: <ScorecardIcon label={formatMessage(messages.componentReadiness)} />,
				message: formatMessage(messages.componentReadiness),
			},
			completedActions: [
				{
					icon: <TeamsIcon label={formatMessage(messages.ownerTeamAdded)} />,
					message: formatMessage(messages.ownerTeamAdded),
				},
				{
					icon: <LinkIcon label={formatMessage(messages.repositoryAdded)} />,
					message: formatMessage(messages.repositoryAdded),
				},
				{
					icon: <AddIcon label={formatMessage(messages.descriptionAdded)} />,
					message: formatMessage(messages.descriptionAdded),
				},
			],
		},
		{
			componentType: 'OTHER',
			scorecard: {
				icon: <ScorecardIcon label={formatMessage(messages.devOpsHealth)} />,
				message: formatMessage(messages.devOpsHealth),
			},
			completedActions: [
				{
					icon: <TeamsIcon label={formatMessage(messages.ownerTeamAdded)} />,
					message: formatMessage(messages.ownerTeamAdded),
				},
				{
					icon: <LinkIcon label={formatMessage(messages.repositoryAdded)} />,
					message: formatMessage(messages.repositoryAdded),
				},
				{
					icon: <PulseIcon label={formatMessage(messages.pullRequestCycleTime)} />,
					message: formatMessage(messages.pullRequestCycleTime),
				},
				{
					message: formatMessage(messages.plusOne),
				},
			],
		},
		{
			componentType: 'OTHER',
			scorecard: {
				icon: <ScorecardIcon label={formatMessage(messages.softwareQuality)} />,
				message: formatMessage(messages.softwareQuality),
			},
			completedActions: [
				{
					icon: <TeamsIcon label={formatMessage(messages.ownerTeamAdded)} />,
					message: formatMessage(messages.ownerTeamAdded),
				},
				{
					icon: <PulseIcon label={formatMessage(messages.buildSuccessRate)} />,
					message: formatMessage(messages.buildSuccessRate),
				},
				{
					icon: <PulseIcon label={formatMessage(messages.openBugs)} />,
					message: formatMessage(messages.openBugs),
				},
				{
					icon: <PulseIcon label={formatMessage(messages.qualityGateConditions)} />,
					message: formatMessage(messages.qualityGateConditions),
				},
			],
		},
		{
			componentType: 'OTHER',
			scorecard: {
				icon: <ScorecardIcon label={formatMessage(messages.openJiraIssues)} />,
				message: formatMessage(messages.openJiraIssues),
			},
			completedActions: [
				{
					icon: <PulseIcon label={formatMessage(messages.openBugs)} />,
					message: formatMessage(messages.openBugs),
				},
				{
					icon: <LinkIcon label={formatMessage(projectAddedGalaxia)} />,
					message: formatMessage(projectAddedGalaxia),
				},
				{
					icon: <TeamsIcon label={formatMessage(messages.ownerTeamAdded)} />,
					message: formatMessage(messages.ownerTeamAdded),
				},
			],
		},
		{
			componentType: 'OTHER',
			scorecard: {
				icon: <ScorecardIcon label={formatMessage(messages.securityMetrics)} />,
				message: formatMessage(messages.securityMetrics),
			},
			completedActions: [
				{
					icon: <PulseIcon label={formatMessage(messages.openCriticalSeverityVulnerabilities)} />,
					message: formatMessage(messages.openCriticalSeverityVulnerabilities),
				},
				{
					icon: <PulseIcon label={formatMessage(messages.openHighSeverityVulnerabilities)} />,
					message: formatMessage(messages.openHighSeverityVulnerabilities),
				},
				{
					message: formatMessage(messages.plusThree),
				},
			],
		},
		{
			componentType: 'OTHER',
			scorecard: {
				icon: <ScorecardIcon label={formatMessage(messages.componentReadiness)} />,
				message: formatMessage(messages.componentReadiness),
			},
			completedActions: [
				{
					icon: <TeamsIcon label={formatMessage(messages.ownerTeamAdded)} />,
					message: formatMessage(messages.ownerTeamAdded),
				},
				{
					icon: <LinkIcon label={formatMessage(messages.repositoryAdded)} />,
					message: formatMessage(messages.repositoryAdded),
				},
				{
					icon: <AddIcon label={formatMessage(messages.descriptionAdded)} />,
					message: formatMessage(messages.descriptionAdded),
				},
			],
		},
	];
	const { repositoryName, cardVisualConfigurationIndex } = props;
	const visualConfigIndex = (cardVisualConfigurationIndex ?? 0) % configuration.length;
	const visualConfig = configuration[visualConfigIndex];
	return (
		<Stack xcss={styles.cardStyles} space="space.050">
			<Inline space="space.050" xcss={styles.containerStyle}>
				<ComponentTypeIcon
					type={toComponentType(visualConfig.componentType ?? 'OTHER')}
					size="medium"
				/>
				<Heading size="small">{repositoryName}</Heading>
			</Inline>
			<Inline space="space.050" xcss={styles.containerStyle}>
				<Box xcss={styles.iconBox}>
					<TeamsIcon label={formatMessage(messages.owner)} />
				</Box>
				<Text size="medium" as="p" align="start">
					{formatMessage(messages.owner)}
				</Text>
			</Inline>
			<Inline space="space.050" xcss={styles.containerStyle}>
				<Box xcss={styles.iconBox}>
					<WorkItemsIcon label={formatMessage(messages.workItems)} />
				</Box>
				<Text size="medium" as="p" align="start">
					{formatMessage(messages.workItems)}
				</Text>
			</Inline>
			<Inline space="space.050" xcss={styles.containerStyle}>
				<Box xcss={styles.iconBox}>{visualConfig.scorecard.icon}</Box>
				<Text size="medium" as="p" align="start">
					{visualConfig.scorecard.message}
				</Text>
				<Box>
					<Tooltip
						position="top"
						component={CustomTooltip}
						content={formatMessage(messages.scorecardInfo)}
					>
						{(tooltipProps) => (
							<IconButton
								testId={`${testId}.scorecard-tooltip`}
								{...tooltipProps}
								icon={InformationCircleIcon}
								label={formatMessage(messages.scorecardInfo)}
								appearance="subtle"
								spacing="compact"
							/>
						)}
					</Tooltip>
				</Box>
			</Inline>
			<Flex wrap="wrap" gap="space.100" xcss={styles.completedActions}>
				{visualConfig.completedActions.map((action, index) => (
					<Inline key={index} xcss={styles.unpressableButton} space="space.050">
						{action.icon ? action.icon : null}
						<Text size="medium" as="p" align="start">
							{action.message}
						</Text>
					</Inline>
				))}
			</Flex>
		</Stack>
	);
}
export default CompassRepositoryCard;
